// GST Payable — Monthly P&L's own simplified running GST ledger. Deliberately
// simpler than the full WHT & GST Control page (gstControlService.js): no
// input credit, no receipt-recognized withholding, no adjustments — just
// carried forward + this month's output GST - what ALM actually paid = a
// running balance. Output GST and the carried-forward figure are always
// derived fresh from approved_invoice_snapshots (never stored redundantly);
// only the "paid by ALM" entries are persisted, the same immutable-entry
// pattern as clientDirectCostService.js / statutoryControlService.js.
import {
  addDoc, collection, getDocs, query, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { activeSnapshotDocs } from "./voidedInvoice";

const SNAPSHOTS = "approved_invoice_snapshots";
const PAID_ENTRIES = "gst_paid_entries";

async function sumOutputGst(constraint) {
  const result = await getDocs(query(collection(db, SNAPSHOTS), constraint));
  return Math.round(activeSnapshotDocs(result).reduce((sum, d) => sum + Number(d.data().totals?.gstAmount || 0), 0));
}

async function sumPaidByAlm(constraint) {
  const result = await getDocs(query(collection(db, PAID_ENTRIES), constraint));
  return Math.round(result.docs.reduce((sum, d) => sum + Number(d.data().amount || 0), 0));
}

export async function recordGstPaidByAlm({ monthKey, amount, paymentDate, referenceNumber, description }) {
  if (!monthKey) throw new Error("Enter the accounting period (YYYY-MM).");
  const value = Math.round(Number(amount || 0));
  if (!(value > 0)) throw new Error("Enter an amount greater than zero.");
  await addDoc(collection(db, PAID_ENTRIES), {
    monthKey,
    amount: value,
    paymentDate: paymentDate || `${monthKey}-01`,
    referenceNumber: String(referenceNumber || "").trim(),
    description: String(description || "").trim(),
    enteredByUid: auth.currentUser?.uid || null,
    enteredByEmail: auth.currentUser?.email || null,
    enteredAt: serverTimestamp()
  });
}

export async function listGstPaidEntries(monthKey) {
  const result = await getDocs(query(collection(db, PAID_ENTRIES), where("monthKey", "==", monthKey)));
  return result.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// carriedForward = every prior month's output GST minus every prior month's
// paid-by-ALM entries, computed in two range queries (accountingPeriod/
// monthKey are "YYYY-MM" strings, so lexicographic "<" is chronological)
// rather than walking back month by month.
export async function getGstPayableSummary(monthKey) {
  const [thisMonthPayable, carriedForwardPayable, paidEntries, carriedForwardPaid] = await Promise.all([
    sumOutputGst(where("accountingPeriod", "==", monthKey)),
    sumOutputGst(where("accountingPeriod", "<", monthKey)),
    listGstPaidEntries(monthKey),
    sumPaidByAlm(where("monthKey", "<", monthKey))
  ]);
  const paidThisMonth = Math.round(paidEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0));
  const carriedForward = carriedForwardPayable - carriedForwardPaid;
  const balance = carriedForward + thisMonthPayable - paidThisMonth;
  return { monthKey, carriedForward, thisMonthPayable, paidThisMonth, paidEntries, balance };
}
